import { useReactiveVar } from "@apollo/client";
import { currentConfigVar } from "apollo/cache/config";
import { Avatar } from "components/core";
import lodash from "lodash";
import moment from "moment";
import { FC } from "react";

interface PersonFields {
  _id?: string;
  code?: string;
  lastName?: string;
  firstName?: string;
  profileImageUrl?: string;
  phoneNumber?: string;
  emailAddress?: string;
  certificationNumber?: string;
}

interface RATServiceOrderDetailsProps {
  serviceOrder: any
}

function Owner({ owner }: { owner?: PersonFields }) {
  return (
    <div className="border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 relative rounded-lg border px-3 py-3 shadow-sm flex items-center space-x-3">
      <div className="flex-shrink-0">
        <Avatar
          alt={
            [(owner?.lastName || "")?.trim(), (owner?.firstName || "")?.trim()]
              .join(" ")
              .trim() || "N A"
          }
          src={owner?.profileImageUrl || ""}
        />
      </div>
      <div className="flex-1 min-w-0 text-left">
        <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
          {[(owner?.lastName || "")?.trim(), (owner?.firstName || "")?.trim()]
            .join(" ")
            .trim() || "N A"}
        </p>
        <p className="text-sm text-gray-500 dark:text-gray-400 truncate">
          {owner?.phoneNumber || "N/A"}
        </p>
      </div>
    </div>
  );
}

export const RATServiceOrderDetails: FC<RATServiceOrderDetailsProps> = ({
  serviceOrder,
}) => {
  const { dateFormat } = useReactiveVar(currentConfigVar);

  return (
    <div className="space-y-6 divide-y divide-gray-200">
      <div>
        <span className="text-xs font-light">Service Order Information</span>
        <div className="grid grid-cols-3 gap-6 mt-2">
          <div>
            <span className="block text-sm font-light text-gray-700">Code</span>
            <div className="mt-1 block w-full sm:text-sm">
              {serviceOrder?.code || "N/A"}
            </div>
          </div>
          <div>
            <span className="block text-sm font-light text-gray-700">Date</span>
            <div className="mt-1 block w-full sm:text-sm">
              {serviceOrder?.createdAt
                ? moment(serviceOrder.createdAt).format(dateFormat)
                : "N/A"}
            </div>
          </div>
          <div>
            <span className="block text-sm font-light text-gray-700">
              Status
            </span>
            <div className="mt-1 block w-full sm:text-sm">
              {lodash.startCase(serviceOrder?.status) || "N/A"}
            </div>
          </div>
          <div>
            <span className="block text-sm font-light text-gray-700">
              Priority
            </span>
            <div className="mt-1 block w-full sm:text-sm">
              {lodash.startCase(serviceOrder?.priority) || "N/A"}
            </div>
          </div>
          {serviceOrder?.result && (
            <div>
              <span className="block text-sm font-light text-gray-700">
                Result
              </span>
              <div className="mt-1 block w-full sm:text-sm">
                {serviceOrder.result}
              </div>
            </div>
          )}
          <div className="col-span-2">
            <span className="block text-sm font-light text-gray-700">
              Appointment Date & Time
            </span>
            <div className="mt-1 block w-full sm:text-sm">
              {serviceOrder?.estimatedResolutionDate
                ? moment(serviceOrder.estimatedResolutionDate).format("DD/MM/YYYY [at] hh:mm A")
                : "N/A"}
            </div>
          </div>
        </div>
      </div>
      <div className="pt-6">
        <span className="text-xs font-light">Contact Person Information</span>
        <div className="grid grid-cols-3 gap-6 mt-2">
          <div className="col-span-2">
            <span className="block text-sm font-light text-gray-700">
              Full Name
            </span>
            <div className="mt-1 block w-full sm:text-sm">
              {serviceOrder?.contactPerson?.fullName || "N/A"}
            </div>
          </div>
          <div>
            <span className="block text-sm font-light text-gray-700">
              Phone Number
            </span>
            <div className="mt-1 block w-full sm:text-sm">
              {serviceOrder?.contactPerson?.phoneNumber || "N/A"}
            </div>
          </div>
        </div>
      </div>
      <div className="pt-6">
        <span className="text-xs font-light">Location Information</span>
        <div className="grid grid-cols-3 gap-6 mt-2">
          <div>
            <span className="block text-sm font-light text-gray-700">
              Region
            </span>
            <div className="mt-1 block w-full sm:text-sm">
              {serviceOrder?.region?.name || "N/A"}
            </div>
          </div>
          <div>
            <span className="block text-sm font-light text-gray-700">
              District
            </span>
            <div className="mt-1 block w-full sm:text-sm">
              {serviceOrder?.district?.name || "N/A"}
            </div>
          </div>
          <div>
            <span className="block text-sm font-light text-gray-700">
              Partner
            </span>
            <div className="mt-1 block w-full sm:text-sm">
              {serviceOrder?.partner?.name || "N/A"}
            </div>
          </div>
          <div className="col-span-3">
            <span className="block text-sm font-light text-gray-700">
              Digital Address
            </span>
            <div className="mt-1 block w-full sm:text-sm">
              {serviceOrder?.digitalAddress || "N/A"}
            </div>
          </div>
        </div>
      </div>
      {serviceOrder?.notes && (
        <div className="pt-6">
          <span className="text-xs font-light">Notes</span>
          <div className="mt-2 block w-full sm:text-sm">
            {serviceOrder.notes}
          </div>
        </div>
      )}
      {serviceOrder?.status === "Cancelled" && (
        <div className="pt-6">
          <span className="text-xs font-light">Cancellation Details</span>
          <div className="grid grid-cols-3 gap-6 mt-2">
            <div>
              <span className="block text-sm font-light text-gray-700">
                Cancelled At
              </span>
              <div className="mt-1 block w-full sm:text-sm">
                {serviceOrder?.cancelledAt
                  ? moment(serviceOrder.cancelledAt).format(dateFormat)
                  : "N/A"}
              </div>
            </div>
            <div>
                <span className="block text-sm font-light text-gray-700">
                  Cancelled By
                </span>
                <div className="mt-1 block w-full sm:text-sm">
                  <Owner owner={serviceOrder?.cancelledBy} />
                </div>
              </div>
            <div className="col-span-2">
              <span className="block text-sm font-light text-gray-700">
                Reason
              </span>
              <div className="mt-1 block w-full sm:text-sm">
                {serviceOrder?.cancellationReason || "N/A"}
              </div>
            </div>
          </div>
        </div>
      )}
      {serviceOrder?.status !== "Pending" && serviceOrder?.status !== "Cancelled" && (
        <div className="pt-6">
          <span className="text-xs font-light">Actors Information</span>
          <div className="grid grid-cols-2 gap-6 mt-2">
            <div>
              <span className="block text-sm font-light text-gray-700">
                Assigner
              </span>
              <div className="mt-1 block w-full sm:text-sm">
                <Owner owner={serviceOrder?.assigner} />
              </div>
            </div>
            <div>
              <span className="block text-sm font-light text-gray-700">
                Assignee (CEWP)
              </span>
              <div className="mt-1 block w-full sm:text-sm">
                <Owner owner={serviceOrder?.assignee} />
              </div>
            </div>
            {serviceOrder?.status === "Disapproved" && (
              <div>
                <span className="block text-sm font-light text-gray-700">
                  Disapprover
                </span>
                <div className="mt-1 block w-full sm:text-sm">
                  <Owner owner={serviceOrder?.disapprover} />
                </div>
              </div>
            )}
            {serviceOrder?.status === "Completed" && (
              <div>
                <span className="block text-sm font-light text-gray-700">
                  Approver
                </span>
                <div className="mt-1 block w-full sm:text-sm">
                  <Owner owner={serviceOrder?.approver} />
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default RATServiceOrderDetails;
